import {
  Dialog as MuiDialog,
  DialogProps,
  dialogTitleClasses,
  Slide,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { usePreventScroll } from '@react-aria/overlays';
import { forwardRef, ReactElement, ReactNode, Ref } from 'react';

import { SiteLocale } from 'constants/i18n.constants';
import { useLocale } from 'hooks/useLocale';

import DialogActions from './DialogActions';
import { transitionDirection } from './types';

const SlideUpTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: ReactElement<unknown, string> },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const SlideLeftTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: ReactElement<unknown, string> },
  ref: Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});
const SlideRightTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: ReactElement<unknown, string> },
  ref: Ref<unknown>,
) {
  return <Slide direction="right" ref={ref} {...props} />;
});

interface Props extends DialogProps {
  children: ReactNode;
  transitionDirection?: transitionDirection;
  onClose?: () => void;
  disableTransition?: boolean;
}

const Dialog = ({
  children,
  transitionDirection = 'up',
  open,
  disableTransition,
  onClose,
  ...dialogProps
}: Props) => {
  usePreventScroll({ isDisabled: !open });
  const { locale } = useLocale();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );
  const TransitionComponent =
    transitionDirection === 'up'
      ? SlideUpTransition
      : locale === SiteLocale.EN
        ? SlideLeftTransition
        : SlideRightTransition;

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullScreen={isMobile}
      fullWidth
      slots={{
        transition: isMobile ? TransitionComponent : undefined,
      }}
      transitionDuration={disableTransition ? 0 : undefined}
      sx={(theme) => ({
        [`& .${dialogTitleClasses.root}`]: {
          padding: theme.spacing(3, 8, 3, 2),
          [theme.breakpoints.up('md')]: {
            padding:
              theme.direction === 'ltr'
                ? theme.spacing(4, 10, 4, 3)
                : theme.spacing(3.5, 10, 4, 3),
          },
        },
        ...(transitionDirection === 'left' && {
          [`& .${dialogTitleClasses.root}`]: {
            padding: theme.spacing(3, 8, 3, 10),
            [theme.breakpoints.up('md')]: {
              padding:
                theme.direction === 'ltr'
                  ? theme.spacing(4, 10, 4, 11)
                  : theme.spacing(3.5, 10, 4, 11),
            },
          },
        }),
      })}
      {...dialogProps}
    >
      {!!onClose && (
        <DialogActions
          transitionDirection={transitionDirection}
          onClose={onClose}
        />
      )}

      {children}
    </MuiDialog>
  );
};

export default Dialog;
